import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBuilding } from "../context/buildingContext";
import { fetchApi } from "../utils/fetchApi";
import { useUser } from "../context/userContext";
import { useRooms } from "../context/RoomsContext";
import { getCurrentSubscription, isNotifEnabledFrom, registerPush } from '../utils/pushNotification';
import { displayFloorAndDoorByName } from '../utils/helpers';
import iconCross from '../assets/icon-cross.svg';
import iconCheck from '../assets/icon-check.svg';

const VAPID_PUBLIC_KEY = "BMYhLrCePlacxZOJ3SF9daM3qeX9W6zyJFAWY-_jVtmMl81C_K9lIAjg7fSWN8jwEloYIpu2DM_5ouI1JwV-d-c";

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const { buildingId, apartmentId, apartmentName } = useBuilding();
  const { rooms, setRooms } = useRooms();
  const navigate = useNavigate();
  const { logout, unit, toggleUnit } = useUser();
  const { clearBuildingData } = useBuilding();

  const [isChildLockEnabled, setChildLockEnabled] = useState<boolean | null>(null);
  const [isDehumidifierEnabled, setIsDehumidifierEnabled] = useState<boolean | null>(null);
  const [isNotificationsEnabled, setIsNotificationsEnabled] = useState<boolean>(false);
  const [isSystemOn, setIsSystemOn] = useState<boolean>(true);
  const [isBuildingDisabled, setIsBuildingDisabled] = useState<boolean>(false);
  const [notifLoading, setNotifLoading] = useState<boolean>(true);
  const [building, setBuilding] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);


  // induláskor: olvassuk ki a tényleges subscription-t
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const sub = await getCurrentSubscription();
      if (!cancelled) {
        setIsNotificationsEnabled(isNotifEnabledFrom(sub));
        setNotifLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (rooms.length > 0) {
      const uniqueChildLocks = new Set(rooms.map((room) => room.childLock));
      if (uniqueChildLocks.size === 1) {
        setChildLockEnabled((prev) => (prev === null ? rooms[0].childLock : prev));
      } else {
        setChildLockEnabled(true);
      }
    }
    setLoading(false);
  }, [rooms]);

  useEffect(() => {
    const fetchApartmentData = async () => {
      if (!apartmentId) return;

      try {
        const response = await fetchApi("get_apartment_data", {
          apartment_id: apartmentId,
        });
        if (response.dehumidificationEquipment !== 0) {
          setIsDehumidifierEnabled(response.airDryingMaxLevel !== 0 && response.airDryingMaxLevel !== null);
        } else {
          setIsDehumidifierEnabled(null);
        }

        // Set system on/off state: use disabledCommand if available, otherwise disabled
        const disabledValue = response.disabledCommand ?? response.disabled;
        setIsSystemOn(!disabledValue); // Invert because disabled=true means system is off
      } catch (err) {
        setError(t("error.loadingFailed"));
        setIsDehumidifierEnabled(null);
      }
    };

    const fetchBuilding = async () => {
      if (!buildingId) return;

      try {
        const response = await fetchApi("get_building_access_rights", {});
        const keys = ['id', 'country', 'postalCode', 'city', 'street', 'temperature', 'humidity', 'name'];
        const matched = response.find((item: any[]) => item[0] === parseInt(buildingId));
        if (matched) {
          const formattedBuilding = keys.reduce((acc, key, index) => {
            acc[key] = matched[index];
            return acc;
          }, {} as Record<string, any>);
          setBuilding(formattedBuilding);
        }

        const buildingData = await fetchApi("get_building_data", { building_id: parseInt(buildingId) });
        if (buildingData.data.disabled === true) {
          setIsBuildingDisabled(true);
          setIsSystemOn(false);
        }
      } catch (error) {
        console.error("Error fetching building data:", error);
      }
    };

    fetchApartmentData();
    fetchBuilding();
  }, [apartmentId, buildingId, t]);


  const handleChildLockToggle = async () => {
    const newValue = !isChildLockEnabled;
    setChildLockEnabled(newValue);

    try {
      await Promise.all(
        rooms.map(async (room) => {
          await fetchApi("update_thermostat_settings", {
            thermostat_id: room.thermostatId,
            child_lock: newValue,
            temp_setpoint_degC: Number(room.temperature),
          });
        })
      );

      setRooms(
        rooms.map((room) => ({
          ...room,
          childLock: newValue,
        }))
      );
    } catch (err) {
      console.error("Error saving child lock:", err);
      setChildLockEnabled(!newValue); // Revert on error
      alert(t("error.savingFailed"));
    }
  };

  const handleDehumidifierToggle = async () => {
    const newValue = !isDehumidifierEnabled;
    setIsDehumidifierEnabled(newValue);

    try {
      await fetchApi("update_apartment_settings", {
        apartment_id: apartmentId,
        air_drying_max_level: newValue ? 1 : 0,
      });
    } catch (err) {
      console.error("Error saving dehumidifier:", err);
      setIsDehumidifierEnabled(!newValue); // Revert on error
      alert(t("error.savingFailed"));
    }
  };

  const handleNotificationsToggle = async () => {
    // ha töltünk, ne engedjük kattintani
    if (notifLoading) return;

    const next = !isNotificationsEnabled;

    if (next) {
      // BEKAPCS: regisztrálunk (ez már hívja a backend 'notification_subscribe'-ot)
      try {
        setNotifLoading(true);
        await registerPush(VAPID_PUBLIC_KEY);
        // ha a regisztráció sikerült, a SW-ben lesz subscription
        const sub = await getCurrentSubscription();
        setIsNotificationsEnabled(isNotifEnabledFrom(sub));
      } catch (e: any) {
        alert('Hiba az értesítések bekapcsolásakor: ' + e.message);
        setIsNotificationsEnabled(false);
      } finally {
        setNotifLoading(false);
      }
    } else {
      // KIKAPCS: leiratkozunk és szólunk a backendnek is
      try {
        setNotifLoading(true);
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();

        const endpoint = sub?.endpoint;
        if (sub) await sub.unsubscribe();

        // Ha a backend oldalán is tárolod, jelezd a leiratkozást (példanév)
        if (endpoint) {
          await fetchApi('notification_subscribe', {
            subscription_obj: JSON.stringify({}),
          });
        }

        setIsNotificationsEnabled(false);
      } catch (e: any) {
        alert('Hiba az értesítések kikapcsolásakor: ' + e.message);
        // ha nem sikerült leiratkozni, próbáljuk szinkronban tartani az UI-t a ténnyel:
        const stillSub = await getCurrentSubscription();
        setIsNotificationsEnabled(isNotifEnabledFrom(stillSub));
      } finally {
        setNotifLoading(false);
      }
    }
  };

  const handleSystemOnToggle = async () => {
    const newValue = !isSystemOn;
    setIsSystemOn(newValue);

    try {
      await fetchApi("update_apartment_settings", {
        apartment_id: apartmentId,
        disabled: !newValue, // Invert because isSystemOn=true means disabled=false
      });
    } catch (err) {
      console.error("Error saving system on/off state:", err);
      setIsSystemOn(!newValue); // Revert on error
      alert(t("error.savingFailed"));
    }
  };

  const handleApartmentSelector = () => {
    navigate("/");
  };

  const handlePasswordChange = () => {
    navigate("/password-change");
  };

  const handleSubUsers = () => {
    navigate("/sub-users");
  };

  const handleLogout = () => {
    clearBuildingData();
    logout();
  };

  if (loading) {
    return <div className="settings"><p>{t("loading")}</p></div>;
  }

  return (
    <>
      <div className="pageHeader">
        <h3>{t('settings')}</h3>
      </div>
      <div className="settings">
        <div className="settingsContainer">
          {/* Apartment Selector Card */}
          <div className="settingsCard" onClick={handleApartmentSelector}>
            <div className="cardContent">
              <div className="cardText">
                <h3 className="cardTextTitle">{t('apartmentSelector')}</h3>
                <p>{building &&
                  `${building.street}. `}
                  {displayFloorAndDoorByName(apartmentName)}
                  {building && `, ${building.city}`}
                </p>
              </div>
              <div className="cardIcon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </div>


          {/* Notifications Card */}
          <div className="settingsCard">
            <div className="cardContent">
              <div className="cardText">
                <h3>{t('enableNotifications')}</h3>
              </div>
              <div className="toggleSwitch">
                <input
                  type="checkbox"
                  id="notifications"
                  checked={isNotificationsEnabled}
                  onChange={handleNotificationsToggle}
                />
                <label htmlFor="notifications" className="toggleLabel">
                  <span className="toggleSlider">
                    <img src={iconCross} alt="off" className="icon icon-cross" />
                    <img src={iconCheck} alt="on" className="icon icon-check" />
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* System On/off Card */}
          <div className="settingsCard">
            <div className="cardContent">
              <div className="cardText">
                <h3>{isSystemOn ? t('isSystemOn') : t('isSystemOff')}</h3>
                {isBuildingDisabled && (
                  <p className="buildingDisabledInfo">{t('buildingDisabled')}</p>
                )}
              </div>
              <div className="toggleSwitch">
                <input
                  type="checkbox"
                  id="isSystemOn"
                  checked={isSystemOn}
                  onChange={handleSystemOnToggle}
                  disabled={isBuildingDisabled}
                />
                <label htmlFor="isSystemOn" className="toggleLabel">
                  <span className="toggleSlider">
                    <img src={iconCross} alt="off" className="icon icon-cross" />
                    <img src={iconCheck} alt="on" className="icon icon-check" />
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Child Lock Card */}
          {rooms.length > 0 && (
            <div className="settingsCard">
              <div className="cardContent">
                <div className="cardText">
                  <h3>{t('childLock')}</h3>
                </div>
                <div className="toggleSwitch">
                  <input
                    type="checkbox"
                    id="childLock"
                    checked={isChildLockEnabled ?? false}
                    onChange={handleChildLockToggle}
                  />
                  <label htmlFor="childLock" className="toggleLabel">
                    <span className="toggleSlider">

                      <img src={iconCross} alt="off" className="icon icon-cross" />

                      <img src={iconCheck} alt="on" className="icon icon-check" />
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Dehumidifier Card */}
          {isDehumidifierEnabled !== null && (
            <div className="settingsCard">
              <div className="cardContent">
                <div className="cardText">
                  <h3>{t('dehumidifierStatus')}</h3>
                </div>
                <div className="toggleSwitch">
                  <input
                    type="checkbox"
                    id="dehumidifier"
                    checked={isDehumidifierEnabled ?? false}
                    onChange={handleDehumidifierToggle}
                  />
                  <label htmlFor="dehumidifier" className="toggleLabel">
                    <span className="toggleSlider">

                      <img src={iconCross} alt="off" className="icon icon-cross" />

                      <img src={iconCheck} alt="on" className="icon icon-check" />
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Temperature Unit Card */}
          <div className="settingsCard">
            <div className="cardContent">
              <div className="cardText">
                <h3>{t('temperatureUnit')}</h3>
              </div>
              <div className="unitSelector">
                <button
                  className={`unitButton firstUnitButton ${unit === 'Celsius' ? 'active' : ''}`}
                  onClick={() => unit !== 'Celsius' && toggleUnit()}
                >
                  °C
                </button>
                <button
                  className={`unitButton secondUnitButton ${unit === 'Fahrenheit' ? 'active' : ''}`}
                  onClick={() => unit !== 'Fahrenheit' && toggleUnit()}
                >
                  °F
                </button>
              </div>
            </div>
          </div>

          {/* Sub Users Card */}
          <div className="settingsCard" onClick={handleSubUsers}>
            <div className="cardContent">
              <div className="cardText">
                <h3>{t('subUsers.label')}</h3>
              </div>
              <div className="cardIcon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </div>

          {/* Password Card */}
          <div className="settingsCard" onClick={handlePasswordChange}>
            <div className="cardContent">
              <div className="cardText">
                <h3>{t('password')}</h3>
              </div>
              <div className="cardIcon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </div>

          {/* Logout Card */}
          <div className="settingsCard logoutCard" onClick={handleLogout}>
            <div className="cardContent">
              <div className="cardText">
                <h3>{t('logOut')}</h3>
              </div>
              <div className="cardIcon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Settings;
